import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useCart } from "../context/CartContext";
import { Check } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { t } = useI18n();
  const { clear } = useCart();
  const [status, setStatus] = useState("checking");
  const [amount, setAmount] = useState(null);
  const pollCount = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    let mounted = true;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (!mounted) return;
        setAmount(r.data.amount);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (r.data.status === "expired" || r.data.status === "failed") {
          setStatus("failed");
          return;
        }
      } catch {}
      pollCount.current += 1;
      if (pollCount.current < 20) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
    return () => { mounted = false; };
  }, [sessionId, clear]);

  return (
    <main className="max-w-[720px] mx-auto px-6 md:px-12 py-28 text-center fade-in" data-testid="payment-success">
      {status === "checking" && (
        <>
          <div className="w-14 h-14 border border-border rounded-full mx-auto mb-8 flex items-center justify-center animate-pulse">
            <div className="w-3 h-3 bg-[hsl(var(--primary))] rounded-full" />
          </div>
          <p className="text-muted-foreground">{t("payment.verifying")}</p>
        </>
      )}
      {status === "paid" && (
        <>
          <div className="w-14 h-14 border border-[hsl(var(--primary))] mx-auto mb-8 flex items-center justify-center">
            <Check strokeWidth={1.25} className="w-6 h-6 text-[hsl(var(--primary))]" />
          </div>
          <div className="label-caps text-muted-foreground mb-4">Order confirmed</div>
          <h1 className="font-hand text-5xl md:text-6xl mb-6" data-testid="success-title">{t("payment.success_title")}</h1>
          <p className="text-muted-foreground mb-4">{t("payment.success_body")}</p>
          {amount != null && (
            <div className="font-hand text-3xl mb-10 text-[hsl(var(--primary))]">
              {Number(amount).toFixed(2).replace(".", ",")} €
            </div>
          )}
          <Link to="/shop" className="btn-primary">{t("payment.continue")}</Link>
        </>
      )}
      {(status === "failed" || status === "timeout") && (
        <>
          <h1 className="font-hand text-5xl md:text-6xl mb-6">{t("payment.cancel_title")}</h1>
          <p className="text-muted-foreground mb-10">{t("payment.cancel_body")}</p>
          <Link to="/cart" className="btn-outline">{t("cart.title")}</Link>
        </>
      )}
    </main>
  );
}
